import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { SUBJECTS } from '@/data/flashcards';
import { useAuth } from '@/context/AuthContext';
import { ChevronRight, BookOpen, Trophy, Target } from 'lucide-react';

const SubjectSelector = ({ onSelectSubject }) => {
  const { user, getSubjectProgress } = useAuth();

  const totalProgress = SUBJECTS.reduce((acc, subject) => {
    const progress = getSubjectProgress(subject.id);
    return acc + progress.correct;
  }, 0);

  const totalCards = SUBJECTS.length * 50;

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Hero Section with Slogan */}
      <div className="px-4 pt-6 pb-4 border-b border-border">
        {/* Logo */}
        <div className="w-16 h-16 mx-auto mb-3 rounded-full overflow-hidden border-2 border-primary shadow-glow">
          <img 
            src="/logo.jpg" 
            alt="S&T Brain Trainer Logo" 
            className="w-full h-full object-cover"
          />
        </div>
        
        {/* Motivational Slogan */}
        <h1 className="text-lg font-display font-bold text-primary text-center leading-tight mb-2">
          Industriemeister bestehen –<br/>nichts dem Zufall überlassen.
        </h1>
        <p className="text-sm text-muted-foreground text-center mb-3">
          Mit Dozenten aus der Praxis. Klar. Strukturiert. Prüfungsnah.
        </p>
        
        {/* Streak Badge */}
        <div className="flex justify-center">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20">
            <Trophy className="w-4 h-4 text-primary" />
            <span className="text-sm font-semibold text-primary">
              {user?.stats?.streak || 0} Tage Streak
            </span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-4 pt-4 pb-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-display font-bold text-foreground">Lernen</h2>
            <p className="text-muted-foreground text-sm">Wähle ein Fach zum Üben</p>
          </div>
        </div>

        {/* Overall Progress Card */}
        <Card className="bg-gradient-card border-border mb-6">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Target className="w-5 h-5 text-primary" />
                <span className="font-medium text-foreground">Gesamtfortschritt</span>
              </div>
              <span className="text-sm font-semibold text-primary">
                {totalProgress} / {totalCards}
              </span>
            </div>
            <Progress 
              value={(totalProgress / totalCards) * 100} 
              className="h-2 bg-muted"
            />
            <p className="text-xs text-muted-foreground mt-2">
              {Math.round((totalProgress / totalCards) * 100)}% aller Karten gemeistert
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Subject Cards */}
      <div className="px-4 space-y-3">
        {SUBJECTS.map((subject, index) => {
          const progress = getSubjectProgress(subject.id);
          const progressPercent = (progress.correct / progress.total) * 100;

          return (
            <Card
              key={subject.id}
              className="bg-card border-border overflow-hidden cursor-pointer hover:border-primary/50 transition-all duration-200 active:scale-[0.98] animate-slide-up"
              style={{ animationDelay: `${index * 0.05}s` }}
              onClick={() => onSelectSubject(subject.id)}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  {/* Subject Icon */}
                  <div className="w-14 h-14 rounded-xl bg-secondary flex items-center justify-center text-2xl">
                    {subject.icon}
                  </div>

                  {/* Subject Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-foreground text-lg">
                        {subject.name}
                      </h3>
                      <span className="text-xs text-muted-foreground px-2 py-0.5 rounded-full bg-muted">
                        50 Karten
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground truncate mb-2">
                      {subject.fullName}
                    </p>
                    
                    {/* Progress Bar */}
                    <div className="flex items-center gap-3">
                      <Progress 
                        value={progressPercent} 
                        className="flex-1 h-1.5 bg-muted"
                      />
                      <span className="text-xs font-medium text-muted-foreground w-10">
                        {progress.correct}/50
                      </span>
                    </div>
                  </div>

                  {/* Arrow */}
                  <ChevronRight className="w-5 h-5 text-muted-foreground" />
                </div>
              </CardContent>
            </Card>
          );
        })})
      </div>

      {/* Quick Stats */}
      <div className="px-4 mt-6">
        <div className="grid grid-cols-3 gap-3">
          <Card className="bg-card border-border">
            <CardContent className="p-3 text-center">
              <BookOpen className="w-5 h-5 text-primary mx-auto mb-1" />
              <p className="text-lg font-bold text-foreground">
                {user?.stats?.totalCards || 0}
              </p>
              <p className="text-xs text-muted-foreground">Gelernt</p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-3 text-center">
              <Target className="w-5 h-5 text-success mx-auto mb-1" />
              <p className="text-lg font-bold text-foreground">
                {user?.stats?.correctCards || 0}
              </p>
              <p className="text-xs text-muted-foreground">Richtig</p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-3 text-center">
              <Trophy className="w-5 h-5 text-primary mx-auto mb-1" />
              <p className="text-lg font-bold text-foreground">
                {user?.stats?.streak || 0}
              </p>
              <p className="text-xs text-muted-foreground">Streak</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SubjectSelector;